import React, { useEffect } from 'react';
import { playNotificationSound } from './utils/notificationSound';
import { BrowserRouter as Router } from 'react-router-dom';
import { AppProvider, useApp } from './context/AppContext';
import { SystemTimeProvider } from './context/SystemTimeContext';
import SocketProvider from './components/SocketProvider';
import Notifications from './components/Notifications';
import MissedCallsNotifications from './components/MissedCallsNotifications';
import UnreadMessagesNotifications from './components/UnreadMessagesNotifications';
import LeaveRequestNotifications from './components/LeaveRequestNotifications';
import TaskNotifications from './components/TaskNotifications';
import KanbanNotifications from './components/KanbanNotifications';
import WorktimeCompletionNotifications from './components/WorktimeCompletionNotifications';
import EmployeeWorktimeCompletedNotification from './components/EmployeeWorktimeCompletedNotification';
import VoiceCallOverlay from './components/VoiceCallOverlay';
import { GlobalStyles, Container } from './styles/GlobalStyles';
import AuthForm from './components/AuthForm';
import Mandatory2faGate from './components/Mandatory2faGate';
import Messenger from './components/Messenger';
import { StyleSheetManager } from 'styled-components';

// Error Boundary для обработки ошибок React
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ 
          padding: '20px', 
          background: '#f8f9fa', 
          border: '1px solid #dee2e6', 
          borderRadius: '8px',
          margin: '20px',
          fontFamily: 'Arial, sans-serif'
        }}>
          <h2 style={{ color: '#dc3545', marginBottom: '10px' }}>Что-то пошло не так</h2>
          <p style={{ color: '#6c757d', marginBottom: '15px' }}>
            Произошла ошибка в приложении. Попробуйте обновить страницу.
          </p>
          <button 
            onClick={() => window.location.reload()}
            style={{
              background: '#007bff',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Обновить страницу
          </button>
          <details style={{ marginTop: '15px' }}>
            <summary style={{ cursor: 'pointer', color: '#6c757d' }}>Детали ошибки</summary>
            <pre style={{ 
              background: '#f8f9fa', 
              padding: '10px', 
              borderRadius: '4px',
              fontSize: '12px',
              overflow: 'auto',
              marginTop: '10px'
            }}>
              {this.state.error?.toString()}
            </pre>
          </details>
        </div>
      );
    }

    return this.props.children;
  }
}

function AppContent() {
  const { state } = useApp();

  // ВЕРСИЯ 5.0 - ЯВНЫЙ МАРКЕР В ЗАГОЛОВКЕ СТРАНИЦЫ
  useEffect(() => {
    document.title = 'Mesendger [V5.0 - 2025-01-20]';
    console.log('%c🚨🚨🚨 ВЕРСИЯ ПРИЛОЖЕНИЯ V5.0 - BUILD 2025-01-20 🚨🚨🚨', 'background: #ff0000; color: #ffffff; font-size: 20px; font-weight: bold; padding: 10px;');
    console.log('%cЕсли вы видите этот лог - новый код загружен!', 'background: #00ff00; color: #000000; font-size: 16px; padding: 5px;');
  }, []);

  // Инициализация синхронизации эмодзи при старте приложения
  useEffect(() => {
    import('./utils/emojiSync.js').then(({ initEmojiSync }) => {
      initEmojiSync().catch(err => {
        console.error('❌ Ошибка инициализации синхронизации эмодзи:', err);
      });
    });
  }, []);

  // Инициализация настроек фронтенда при старте приложения
  useEffect(() => {
    import('./utils/frontendSettings.js').then(({ initializeFrontendSettings }) => {
      initializeFrontendSettings().catch(err => {
        console.error('❌ Ошибка инициализации настроек фронтенда:', err);
      });
    });
  }, []);

  useEffect(() => {
    if (!state?.user?.id) return;
    import('./utils/frontendSettings.js').then(({ initializeFrontendSettings }) => {
      initializeFrontendSettings({ forceServerFetch: true }).catch(err => {
        console.error('❌ Ошибка обновления настроек фронтенда после входа:', err);
      });
    });
  }, [state?.user?.id]);

  useEffect(() => {
    // Тестовый вызов звука при монтировании компонента
    playNotificationSound();
  }, []);

  // Предотвращение масштабирования через touch-жесты
  useEffect(() => {
    const preventZoom = (e) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    };

    // Предотвращаем pinch zoom
    document.addEventListener('touchstart', preventZoom, { passive: false });
    document.addEventListener('touchmove', preventZoom, { passive: false });
    
    // Предотвращаем double-tap zoom
    let lastTouchEnd = 0;
    const preventDoubleTapZoom = (e) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        e.preventDefault();
      }
      lastTouchEnd = now;
    };
    document.addEventListener('touchend', preventDoubleTapZoom, { passive: false });

    // Устанавливаем viewport meta тег программно (на случай если HTML не обновился)
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, minimum-scale=1.0, user-scalable=no, viewport-fit=cover');
    }

    return () => {
      document.removeEventListener('touchstart', preventZoom);
      document.removeEventListener('touchmove', preventZoom);
      document.removeEventListener('touchend', preventDoubleTapZoom);
    };
  }, []);

  // Пинги использования приложения: старт при входе, стоп при закрытии
  useEffect(() => {
    if (!state.isAuthenticated) return;
    const token = localStorage.getItem('token');
    if (token) {
      fetch('/api/app-usage/ping', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ event: 'start' })
      }).catch(() => {});
    }

    const onClose = () => {
      const tk = localStorage.getItem('token');
      if (!tk) return;
      try {
        fetch('/api/app-usage/ping', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tk}` },
          body: JSON.stringify({ event: 'stop' }),
          keepalive: true
        }).catch(() => {});
      } catch {}
    };

    window.addEventListener('beforeunload', onClose);
    window.addEventListener('pagehide', onClose);
    const onVis = () => {
      if (document.visibilityState === 'hidden') onClose();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('beforeunload', onClose);
      window.removeEventListener('pagehide', onClose);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [state.isAuthenticated]);

  if (!state.isAuthenticated) {
    return <AuthForm />;
  }

  return (
    <Mandatory2faGate>
      <SocketProvider>
        <Container>
          <Messenger />
          <Notifications />
          <VoiceCallOverlay />
          {/* Правая колонка уведомлений — сообщения, пропущенные звонки, задачи и рабочее время */}
          <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 2147483647, display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 380, pointerEvents: 'none' }}>
            <div style={{ pointerEvents: 'auto' }}><UnreadMessagesNotifications /></div>
            <div style={{ pointerEvents: 'auto' }}><MissedCallsNotifications /></div>
            <div style={{ pointerEvents: 'auto' }}><LeaveRequestNotifications /></div>
            <div style={{ pointerEvents: 'auto' }}><TaskNotifications /></div>
            <div style={{ pointerEvents: 'auto' }}><KanbanNotifications /></div>
            <div style={{ pointerEvents: 'auto' }}><WorktimeCompletionNotifications /></div>
            <div style={{ pointerEvents: 'auto' }}><EmployeeWorktimeCompletedNotification /></div>
          </div>
        </Container>
      </SocketProvider>
    </Mandatory2faGate>
  );
}

// Глобальная фильтрация пропсов для styled-components
const shouldForwardProp = (prop, defaultValidatorFn) => {
  // Фильтруем кастомные пропсы, которые не должны попадать в DOM
  const customProps = ['active', 'selected', 'online', 'clickable', 'show', 'isGroup'];
  if (customProps.includes(prop)) {
    return false;
  }
  // Для остальных пропсов используем стандартную валидацию или возвращаем true
  return typeof defaultValidatorFn === 'function' ? defaultValidatorFn(prop) : true;
};

function App() {
  return (
    <ErrorBoundary>
      <StyleSheetManager shouldForwardProp={shouldForwardProp}>
        <Router>
          <SystemTimeProvider>
            <AppProvider>
              <GlobalStyles />
              <ErrorBoundary>
                <AppContent />
              </ErrorBoundary>
            </AppProvider>
          </SystemTimeProvider>
        </Router>
      </StyleSheetManager>
    </ErrorBoundary>
  );
}

export default App;